<template>
  <div
    v-for="payment in bill.payments"
    :key="payment.id"
    class="flex py-2 items-center text-sm font-body"
  >
    <div class="flex-1 capitalize text-n-200">
      <div>{{ t(`bill.${payment.type}`, payment.type) }}</div>
      <div v-if="(payment.tip || 0) > 0" class="text-xs ml-2">
        {{ t('checkout.tip') }}
      </div>
    </div>
    <div class="text-right font-bold">
      <div>{{ currency(payment.amount - (payment.tip || 0)) }}</div>
      <div v-if="(payment.tip || 0) > 0">
        {{ currency(payment.tip) }}
      </div>
    </div>
    <l-dropdown
      v-if="isDataphonePayment(payment)"
      trigger="click"
      :mark-selected="false"
      :options="getOptionsDataphone(payment)"
    >
      <l-icon name="3-dots" size="medium" class="text-v-300 p-1 ml-2 cursor-pointer" />
    </l-dropdown>
    <l-icon
      v-else
      name="delete"
      size="medium"
      class="text-v-300 ml-3 p-1 cursor-pointer"
      @click="deletePaymentWithConfirmation(payment)"
    />
  </div>
  <div v-if="bill.payments.length === 0" class="text-n-0 text-sm py-2">
    {{ t('checkout.no-payments') }}
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import { LDropdown, LIcon, useDialog } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import { useMoney } from '@/composables/useMoney'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { useTabsStore } from '@/store/tabs'
import type { BillWithPayments, Payment } from '@/types'

const props = defineProps<{
  bill: BillWithPayments
}>()

const { t } = useI18n()
const { currency } = useMoney()
const dialog = useDialog()
const tabsStore = useTabsStore()

const REFUNDABLE_PAYMENT_METHODS = ['dataphone']

function getOptionsDataphone(payment: Payment): Option[] {
  return [
    {
      label: t('checkout.generate-receipt'),
      value: 'generate-receipt',
      onClick: () => generateBankReceipt(payment)
    },
    {
      label: t('checkout.refund-payment'),
      value: 'refund-payment',
      onClick: () => deletePaymentWithConfirmation(payment)
    }
  ]
}

function isDataphonePayment(payment: Payment) {
  return (
    (payment.type === 'dataphone' && !!payment?.metadata?.receipt) ||
    (payment.type === 'lastpay' && !!payment?.metadata?.SaleToPOIResponse)
  )
}

function generateBankReceipt(payment: Payment) {
  TicketPrinter.printBankReceipt(payment)
}

function deletePaymentWithConfirmation(payment: Payment) {
  const isRefundable = REFUNDABLE_PAYMENT_METHODS.includes(payment.type)

  dialog({
    title: isRefundable ? t('checkout.refund-payment') : t('checkout.delete-payment'),
    content: isRefundable
      ? t('checkout.refund-payment-content')
      : t('checkout.delete-payment-content'),
    secondaryLabel: t('ctas.cancel'),
    onConfirm: () => tabsStore.deletePayment({ billId: props.bill.id, paymentId: payment.id })
  })
}
</script>
